// ── Importaciones tal cual ──────────────────────────────
import React, { useState, useMemo } from 'react';
import ReactPaginate from "react-paginate";
import { Badge } from 'react-bootstrap';
import { RenderIf } from '../../utils/RenderIf';
import { formatDateTime } from '../../utils/formatDate';

/* ─────────────────────────────────────────────────────────
   CSS — inyectado una vez. Prefijo: .rt-
───────────────────────────────────────────────────────── */
const RT_CSS = `
@keyframes rt-up { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }

/* ── wrapper ── */
.rt-wrap {
  background: var(--bg, #fff);
  border: 1.5px solid var(--border, rgba(0,0,0,.07));
  border-radius: 16px; overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,.05);
  margin: 1.25rem 0;
  animation: rt-up .28s ease both;
}

/* ── toolbar ── */
.rt-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: nowrap; gap: .65rem;
  padding: .75rem 1.1rem;
  border-bottom: 1px solid var(--border, rgba(0,0,0,.07));
  background: var(--bg-2, rgba(0,0,0,.02));
  min-width: 0;
}
.rt-title {
  font-size: .88rem; font-weight: 700; color: #1B3A6B;
  font-family: var(--font-sans,-apple-system,sans-serif);
  display: flex; align-items: center; gap: .4rem;
  white-space: nowrap; flex-shrink: 0;
}
.rt-controls {
  display: flex; align-items: center; gap: .45rem;
  flex-wrap: nowrap; flex-shrink: 1; min-width: 0;
}
.rt-label {
  font-size: .72rem; font-weight: 600; color: var(--text-3, #B4B4B4);
  font-family: var(--font-sans,-apple-system,sans-serif); white-space: nowrap;
  flex-shrink: 0;
}

/* btn descargar */
.rt-btn-download {
  display: inline-flex; align-items: center; gap: .35rem;
  padding: .42rem .9rem; border-radius: 8px;
  border: 1.5px solid rgba(22,163,74,.35); background: rgba(22,163,74,.07);
  color: #16A34A;
  font-family: var(--font-sans,-apple-system,sans-serif);
  font-size: .78rem; font-weight: 600; cursor: pointer;
  transition: all .14s; white-space: nowrap;
}
.rt-btn-download:hover { background: #16A34A; color: #fff; border-color: #16A34A; }

/* select resultados por página */
.rt-select {
  padding: .38rem .65rem; border-radius: 8px;
  border: 1.5px solid rgba(0,0,0,.11); background: var(--bg, #fff);
  font-size: .78rem; color: var(--text, #0A0A0A);
  font-family: var(--font-sans,-apple-system,sans-serif); outline: none;
  cursor: pointer; transition: border-color .14s;
}
.rt-select:focus { border-color: #1B3A6B; box-shadow: 0 0 0 3px rgba(27,58,107,.09); }

/* search input */
.rt-search-wrap { position: relative; }
.rt-search-ico {
  position: absolute; left: 9px; top: 50%; transform: translateY(-50%);
  color: var(--text-3, #B4B4B4); pointer-events: none; display: flex;
}
.rt-search {
  padding: .4rem .75rem .4rem 2rem;
  border: 1.5px solid rgba(0,0,0,.11); border-radius: 8px;
  background: var(--bg, #fff); font-size: .78rem;
  color: var(--text, #0A0A0A); outline: none;
  font-family: var(--font-sans,-apple-system,sans-serif);
  transition: border-color .14s, box-shadow .14s; width: 180px;
}
.rt-search:focus { border-color: #1B3A6B; box-shadow: 0 0 0 3px rgba(27,58,107,.09); width: 220px; }
.rt-search::placeholder { color: var(--text-3, #B4B4B4); }
@media(max-width: 560px) { .rt-search,.rt-search:focus { width: 100%; } }

/* ── table ── */
.rt-table-wrap {
  overflow-x: auto;
  scrollbar-width: thin; scrollbar-color: rgba(0,0,0,.07) transparent;
}
.rt-table-wrap::-webkit-scrollbar { height: 4px; }
.rt-table-wrap::-webkit-scrollbar-thumb { background: rgba(0,0,0,.08); border-radius: 2px; }

.rt-wrap .table { margin-bottom: 0; font-size: .81rem; }
.rt-wrap .table thead tr { background: #1B3A6B !important; }
.rt-wrap .table thead th {
  background: #1B3A6B !important; color: #fff !important;
  font-size: .68rem !important; font-weight: 700 !important;
  letter-spacing: .05em; text-transform: uppercase;
  border-color: rgba(255,255,255,.1) !important;
  padding: .6rem .75rem !important; white-space: nowrap;
}
.rt-wrap .table tbody tr { transition: background .1s; }
.rt-wrap .table tbody tr:hover { background: rgba(27,58,107,.04) !important; }
.rt-wrap .table tbody td {
  vertical-align: middle !important;
  font-size: .8rem; color: var(--text-2, #6B6B6B);
  border-color: var(--border, rgba(0,0,0,.06)) !important;
  padding: .52rem .75rem !important;
}
.rt-wrap .table tbody tr:last-child td { border-bottom: none !important; }

/* ── pagination (ReactPaginate override) ── */
.rt-pag-wrap {
  padding: .65rem 1.1rem;
  border-top: 1px solid var(--border, rgba(0,0,0,.07));
  background: var(--bg-2, rgba(0,0,0,.02));
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: .5rem;
}
.rt-pag-info {
  font-size: .72rem; color: var(--text-3, #B4B4B4);
  font-family: var(--font-sans,-apple-system,sans-serif);
}
.rt-pag-info strong { color: var(--text-2, #6B6B6B); }

.rt-wrap .pagination {
  margin: 0; gap: .25rem; flex-wrap: wrap;
}
.rt-wrap .pagination .page-item .page-link {
  min-width: 30px; height: 30px; border-radius: 7px !important;
  border: 1.5px solid var(--border, rgba(0,0,0,.11)) !important;
  background: var(--bg, #fff) !important; color: var(--text-2, #6B6B6B) !important;
  font-size: .75rem; font-weight: 600; display: flex; align-items: center;
  justify-content: center; padding: 0 6px; transition: all .13s;
  font-family: var(--font-sans,-apple-system,sans-serif);
}
.rt-wrap .pagination .page-item .page-link:hover {
  border-color: #1B3A6B !important; color: #1B3A6B !important;
  background: rgba(27,58,107,.07) !important;
}
.rt-wrap .pagination .page-item.active .page-link {
  background: #1B3A6B !important; border-color: #1B3A6B !important;
  color: #fff !important; box-shadow: none !important;
}
.rt-wrap .pagination .page-item.disabled .page-link {
  opacity: .35; cursor: not-allowed;
}

/* ── empty ── */
.rt-empty {
  display: flex; flex-direction: column; align-items: center;
  padding: 2.5rem 1rem; gap: .5rem; text-align: center;
}
.rt-empty-title { font-size: .85rem; font-weight: 700; color: var(--text-2, #6B6B6B); font-family: var(--font-sans,-apple-system,sans-serif); }
.rt-empty-sub   { font-size: .74rem; color: var(--text-3, #B4B4B4); font-family: var(--font-sans,-apple-system,sans-serif); }

/* ── responsive ── */
@media(max-width: 700px) {
  /* toolbar en 2 filas: título arriba, controles abajo */
  .rt-toolbar {
    flex-direction: column; align-items: stretch; gap: .55rem;
    padding: .75rem .9rem;
  }
  .rt-title { font-size: .84rem; }

  /* fila de controles: todos en una línea compacta */
  .rt-controls {
    display: flex; flex-wrap: nowrap;
    align-items: center; gap: .35rem;
    overflow-x: auto; scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }
  .rt-controls::-webkit-scrollbar { display: none; }

  /* ocultar label "Mostrar" y "Buscar" en mobile para ahorrar espacio */
  .rt-label { display: none; }

  /* select más compacto */
  .rt-select { width: 60px !important; flex-shrink: 0; font-size: .74rem; padding: .35rem .4rem; }

  /* search no se expande al hacer focus en mobile */
  .rt-search, .rt-search:focus { width: 120px; flex-shrink: 1; min-width: 80px; }

  .rt-pag-wrap { justify-content: center; }
  .rt-pag-info { width: 100%; text-align: center; }
}
`;

/* ── inject helper (fired once inside component) ── */
let RT_CSS_INJECTED = false;
const injectRtCss = () => {
  if (RT_CSS_INJECTED) return;
  RT_CSS_INJECTED = true;
  const id = "rt-styles";
  if (!document.getElementById(id)) {
    const el = document.createElement("style");
    el.id = id; el.textContent = RT_CSS;
    document.head.appendChild(el);
  }
};

/* ── ICONS ── */
const IcoSearch   = ()=><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const IcoDownload = ()=><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
const IcoTable    = ()=><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="9" x2="9" y2="21"/></svg>;

/* ════════════════════════════════════════════════════════
   COMPONENT — lógica 100% original
════════════════════════════════════════════════════════ */
const ReportTable = ({
  table_name, table_data, table_headers, table_type,
  handleShowModalDetail, setSelectedInformZ, getInformZById, downloadReport
}) => {

  injectRtCss();

  /* ════════════════════════════════════
     LÓGICA ORIGINAL — 100% INTACTA
  ════════════════════════════════════ */
  const [searchTerm, setSearchTerm]     = useState('');
  const [dataPage, setDataPagePage]     = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  let infoDataPerPage = 5;

  const tableHeaders = () => {
    const headers = table_headers.map((header) => (
      <th key={header}>{header}</th>
    ));
    return headers;
  };

  const handlePageChange = ({ selected }) => { setDataPagePage(selected); };

  const filteredData = useMemo(() => {
    if (searchTerm === '') return table_data;
    return table_data.filter(data => {
      if (table_type === "general_report") {
        const transactionMatch = data.transaction_code.toString().toLowerCase().includes(searchTerm.toLocaleLowerCase());
        const dateMatch = data.createdAt.toLowerCase().includes(searchTerm.toLowerCase());
        return transactionMatch || dateMatch;
      } else if (table_type === "paymentmethod_report") {
        const transactionMatch = data.transaction_code.toString().toLowerCase().includes(searchTerm.toLocaleLowerCase());
        const dateMatch = data.createdAt.toLowerCase().includes(searchTerm.toLowerCase());
        const paymentmethodMatch = data.paymenthod.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        return transactionMatch || dateMatch || paymentmethodMatch;
      } else if (table_type === "products_report") {
        const productMatch = data.group_name.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        const totalAmountProductMatch = data.total_amount_selled.toString().toLowerCase().includes(searchTerm.toLocaleLowerCase());
        return productMatch || totalAmountProductMatch;
      } else if (table_type === "inventory_report") {
        const productMatch = data.group_name.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        return productMatch;
      } else if (table_type === "movements_report") {
        const movement_typeMatch = data.movement_type.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        const productMatch = data.group_name.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        const condition_movement = data.condition_movement.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        return productMatch || movement_typeMatch || condition_movement;
      } else if (table_type === "returns_report") {
        const transactionMatch = data.transaction_code.toString().toLowerCase().includes(searchTerm.toLocaleLowerCase());
        const dateMatch = data.createdAt.toLowerCase().includes(searchTerm.toLowerCase());
        const userMatch = data.user.name.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        return transactionMatch || dateMatch || userMatch;
      } else if (table_type === "tips_report") {
        const transactionMatch = data.transaction_code.toString().toLowerCase().includes(searchTerm.toLocaleLowerCase());
        const dateMatch = data.createdAt.toLowerCase().includes(searchTerm.toLowerCase());
        return transactionMatch || dateMatch;
      } else if (table_type === "informz_report") {
        const dateMatch = data.createdAt.toLowerCase().includes(searchTerm.toLowerCase());
        const code = data.code.toString().toLowerCase().includes(searchTerm.toLocaleLowerCase());
        return dateMatch || code;
      }
    });
  }, [searchTerm, table_data]);

  let tableDataInfoPerPage = filteredData.slice(dataPage * itemsPerPage, (dataPage + 1) * itemsPerPage);

  const tableContent = () => {
    if (table_type === "general_report") {
      const general_sales_content = tableDataInfoPerPage.map((content) => (
        <tr key={content.transaction_code}>
          <td className='align-middle'>{content.createdAt.split('T')[0]}</td>
          <td className='align-middle'>{content.createdAt.split('T')[1].split(':')[0] + ':' + content.createdAt.split('T')[1].split(':')[1] + ' ' + (content.createdAt.split('T')[1].split(':')[0] <= 11 ? 'AM' : 'PM')}</td>
          <td className='align-middle'>{content.transaction_code}</td>
          <td className="aling-middle">{content.total_with_tips === undefined ? "$ 0.00" : "$ " + content.total_with_tips.toFixed(2)}</td>
          <td className="aling-middle">{content.tips === undefined ? "$ 0.00" : "$ " + content.tips.toFixed(2)}</td>
          <td className='align-middle'>{content.total === undefined ? "$ 0.00" : "$ " + content.total.toFixed(2)}</td>
          <td className='align-middle'>{content.tax === undefined ? "$ 0.00" : "$ " + content.tax.toFixed(2)}</td>
          <td className='align-middle'>{content.delivery === undefined ? "$ 0.00" : content.delivery.value !== undefined ? "$ " + content.delivery.value.toFixed(2) : "$ 0.00"}</td>
          <td className='align-middle'>{content.subtotal === undefined ? "$ 0.00" : "$ " + content.subtotal.toFixed(2)}</td>
          <td className='align-middle'>{content.discount === undefined || content.discount === "" ? "$ 0.00" : "$ " + content.discount.toFixed(2)}</td>
          <td className='align-middle'>{content.cufe === undefined ? "0" : content.cufe}</td>
        </tr>
      ));
      return general_sales_content;
    } else if (table_type === "paymentmethod_report") {
      const paymentmethod_sales_content = tableDataInfoPerPage.map((content) => (
        <tr key={content.transaction_code}>
          <td className='align-middle'>{content.createdAt.split('T')[0]}</td>
          <td className='align-middle'>{content.createdAt.split('T')[1].split(':')[0] + ':' + content.createdAt.split('T')[1].split(':')[1] + ' ' + (content.createdAt.split('T')[1].split(':')[0] <= 11 ? 'AM' : 'PM')}</td>
          <td className='align-middle'>{content.transaction_code}</td>
          <td className='aling-middle'>{content.paymenthod}</td>
          <td className="aling-middle">{content.total_with_tips === undefined ? "$ 0.00" : "$ " + content.total_with_tips.toFixed(2)}</td>
          <td className="aling-middle">{content.tips === undefined ? "$ 0.00" : "$ " + content.tips.toFixed(2)}</td>
          <td className='align-middle'>{content.total === undefined ? "$ 0.00" : "$ " + content.total.toFixed(2)}</td>
          <td className='align-middle'>{content.subtotal === undefined ? "$ 0.00" : "$ " + content.subtotal.toFixed(2)}</td>
          <td className='align-middle'>{content.tax === undefined ? "$ 0.00" : "$ " + content.tax.toFixed(2)}</td>
          <td className='align-middle'>{content.discount === undefined ? "$ 0.00" : "$ " + content.discount.toFixed(2)}</td>
          <td className='align-middle'>{content.cufe === undefined ? "0" : content.cufe}</td>
        </tr>
      ));
      return paymentmethod_sales_content;
    } else if (table_type === "products_report") {
      const product_sales_content = tableDataInfoPerPage.map((content) => (
        <tr key={content.group_name}>
          <td className='align-middle'>{content.group_name}</td>
          <td className='align-middle'>{content.total_amount_selled === undefined ? "0" : content.total_amount_selled}</td>
          <td className='align-middle'>{content.total_selled === undefined ? "$ 0.00" : '$ ' + content.total_selled.toFixed(2)}</td>
        </tr>
      ));
      return product_sales_content;
    } else if (table_type === 'preaccount_report') {
      const preaccount_report = tableDataInfoPerPage.map((content) => (
        <tr key={content.code}>
          <td className='align-middle'>{content.createdAt.split('T')[0]}</td>
          <td className='align-middle'>{content.createdAt.split('T')[1].split(':')[0] + ':' + content.createdAt.split('T')[1].split(':')[1] + ' ' + (content.createdAt.split('T')[1].split(':')[0] <= 11 ? 'AM' : 'PM')}</td>
          <td className='align-middle'>{content.code}</td>
          <td className='align-middle'>{content.total === undefined ? "$ 0.00" : "$ " + parseFloat(content.total).toFixed(2)}</td>
          <td className='align-middle'>{content.subtotal === undefined ? "$ 0.00" : "$ " + parseFloat(content.subtotal).toFixed(2)}</td>
          <td className='align-middle'>{content.tax === undefined ? "$ 0.00" : "$ " + parseFloat(content.tax).toFixed(2)}</td>
          <td className='align-middle'>{content.discount === undefined ? "$ 0.00" : "$ " + parseFloat(content.discount).toFixed(2)}</td>
        </tr>
      ));
      return preaccount_report;
    } else if (table_type === 'discount_report') {
      const discount_report = tableDataInfoPerPage.map((content) => (
        <tr key={content.transaction_code}>
          <td className='align-middle'>{content.createdAt.split('T')[0]}</td>
          <td className='align-middle'>{content.createdAt.split('T')[1].split(':')[0] + ':' + content.createdAt.split('T')[1].split(':')[1] + ' ' + (content.createdAt.split('T')[1].split(':')[0] <= 11 ? 'AM' : 'PM')}</td>
          <td className='align-middle'>{content.transaction_code}</td>
          <td className='align-middle'>{content.total === undefined ? "$ 0.00" : "$ " + content.total.toFixed(2)}</td>
          <td className='align-middle'>{content.approvedby === undefined ? "No registrado" : content.approvedby}</td>
          <td className='align-middle'>{content.user === null && content.client === null ? "Cliente generico" : content.user === null && content.client !== null ? content.client : content.user !== null && content.client === null ? content.user : 'Cliente generico'}</td>
        </tr>
      ));
      return discount_report;
    } else if (table_type === 'returns_report') {
      const returns_report = tableDataInfoPerPage.map((content) => (
        <tr key={content.transaction_code}>
          <td className='align-middle'>{content.createdAt.split('T')[0]}</td>
          <td className='align-middle'>{content.createdAt.split('T')[1].split(':')[0] + ':' + content.createdAt.split('T')[1].split(':')[1] + ' ' + (content.createdAt.split('T')[1].split(':')[0] <= 11 ? 'AM' : 'PM')}</td>
          <td className='align-middle'>{content.transaction_code}</td>
          <td className='align-middle'>{content.total === undefined ? "$ 0.00" : "$ " + content.total.toFixed(2)}</td>
          <td className='align-middle'>{content.user.name === undefined || content.user.name === "0" ? "No registrado" : content.user.name}</td>
          <td className='align-middle'>{content.devolution_order_reference === undefined || content.devolution_order_reference.approved_by === undefined || content.devolution_order_reference.approved_by.name === undefined ? "No registrado" : content.devolution_order_reference.approved_by.name}</td>
        </tr>
      ));
      return returns_report;
    } else if (table_type === "tips_report") {
      const tips_report = tableDataInfoPerPage.map((content) => (
        <tr key={content.transaction_code}>
          <td className='align-middle'>{content.createdAt.split('T')[0]}</td>
          <td className='align-middle'>{content.createdAt.split('T')[1].split(':')[0] + ':' + content.createdAt.split('T')[1].split(':')[1] + ' ' + (content.createdAt.split('T')[1].split(':')[0] <= 11 ? 'AM' : 'PM')}</td>
          <td className='align-middle'>{content.transaction_code}</td>
          <td className='align-middle'>{content.total === undefined ? "$ 0.00" : "$ " + content.total.toFixed(2)}</td>
          <td className='align-middle'>{content.tips === undefined ? "$ 0.00" : "$ " + content.tips.toFixed(2)}</td>
        </tr>
      ));
      return tips_report;
    } else if (table_type === 'inventory_report') {
      const inventory_report = tableDataInfoPerPage.map((content) => (
        <tr key={content.group_name}>
          <td className='align-middle'>{content.group_name}</td>
          <td className='align-middle'>{content.quantity}</td>
        </tr>
      ));
      return inventory_report;
    } else if (table_type === 'movements_report') {
      const inventory_report = tableDataInfoPerPage.map((content) => (
        <tr key={content.group_name + content.created_at}>
          <td className='align-middle'>{content.group_name}</td>
          <td className='align-middle'>{content.quantity}</td>
          <td className='align-middle'>{content.movement_type === "exit" ? "Salida" : content.movement_type === "entry" ? "Entrada" : content.movement_type === "transfer" ? "Transferencia" : content.movement_type}</td>
          <td className='align-middle' style={{ maxWidth: '220px', whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
            {content.reason === "Inventory received" && content.movement_status == null && content.movement_type === "entry" ?
              "Entrada de inventario manual"
              : content.reason === "Inventory transferred" && content.movement_status == "approved" && content.movement_type === "transfer" ?
                "Solicitud de transferencia aprobada" :
                content.reason === "Inventory transferred" && content.movement_status == "rejected" && content.movement_type === "transfer" ?
                  "Solicitud de transferencia rechazada" :
                  content.reason === "Inventory transferred" && content.movement_status == "pending" && content.movement_type === "transfer" ?
                    "Transferencia creada, pendiente por revisar " :
                    content.reason === "Inventory transferred" && content.movement_status == null && content.movement_type === "exit" ?
                      "Salida de inventario por transferencia" :
                      content.reason === "Inventory transferred" && content.movement_status == null && content.movement_type === "entry" ?
                        "Entrada de inventario por transferencia" :
                        content.reason}
          </td>
          <td className='align-middle'>{content.note == null ? <>Vacio</> : <>{content.note}</>}</td>
          <td className='align-middle'>{content.source_warehouse_name}</td>
          <td className='align-middle'>{content.destination_warehouse_name}</td>
          <td className='align-middle'>{content.user_name}</td>
          <td className='align-middle'>{formatDateTime(content.created_at)}</td>
        </tr>
      ));
      return inventory_report;
    } else if (table_type === 'informz_report') {
      const informz_report = tableDataInfoPerPage.map((content) => (
        <tr key={content.code}>
          <td className='align-middle'>{content.createdAt.split('T')[0]}</td>
          <td className='align-middle'>{content.createdAt.split('T')[1].split(':')[0] + ':' + content.createdAt.split('T')[1].split(':')[1] + ' ' + (content.createdAt.split('T')[1].split(':')[0] <= 11 ? 'AM' : 'PM')}</td>
          <td className='align-middle'>{content.code}</td>
          <td className='align-middle'>
            <button
              className='btn btn-primary border-0'
              onClick={() => { handleShowModalDetail(); setSelectedInformZ(content); getInformZById(content._id); }}
            >
              <i className='uil uil-eye' />
            </button>
          </td>
        </tr>
      ));
      return informz_report;
    }
  };
  /* ════════════════════════════════════
     FIN LÓGICA ORIGINAL
  ════════════════════════════════════ */

  /* ── totals for pag info ── */
  const startItem = filteredData.length === 0 ? 0 : dataPage * itemsPerPage + 1;
  const endItem   = Math.min((dataPage + 1) * itemsPerPage, filteredData.length);
  const pageCount = Math.ceil(filteredData.length / itemsPerPage);

  /* ══════════════════════════════════
     RENDER
  ══════════════════════════════════ */
  return (
    <div className="rt-wrap">

      {/* ── TOOLBAR ── */}
      <div className="rt-toolbar">
        <div className="rt-title"><IcoTable /> {table_name}</div>

        <div className="rt-controls">
          {/* botón descargar — lógica original intacta */}
          <RenderIf isTrue={table_type === "inventory_report" || table_type === "movements_report"}>
            <button className="rt-btn-download btn btn-outline-success" onClick={() => downloadReport()}>
              <IcoDownload /> Descargar Excel
            </button>
          </RenderIf>

          {/* resultados por página — lógica original intacta */}
          <span className="rt-label">Mostrar</span>
          <select
            className="rt-select form-select"
            value={itemsPerPage}
            onChange={e => { setItemsPerPage(Number(e.target.value)); setDataPagePage(0); }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>

          {/* buscador — lógica original intacta */}
          <div className="rt-search-wrap">
            <div className="rt-search-ico"><IcoSearch /></div>
            <input
              spellCheck="false"
              className="rt-search form-control"
              type="text"
              step="any"
              placeholder="Buscar…"
              onChange={e => { setSearchTerm(e.target.value); setDataPagePage(0); }}
            />
          </div>
        </div>
      </div>

      {/* ── TABLE ── */}
      <div className="rt-table-wrap">
        {filteredData.length === 0 ? (
          <div className="rt-empty">
            <p className="rt-empty-title">Sin resultados</p>
            <p className="rt-empty-sub">{searchTerm ? `No hay coincidencias para "${searchTerm}"` : 'No hay datos disponibles para esta consulta.'}</p>
          </div>
        ) : (
          <table className='table table-hover table-sm'>
            <thead className='table-dark'>
              <tr>{tableHeaders()}</tr>
            </thead>
            <tbody>
              {tableContent()}
            </tbody>
          </table>
        )}
      </div>

      {/* ── PAGINATION ── */}
      {filteredData.length > 0 && (
        <div className="rt-pag-wrap">
          <span className="rt-pag-info">
            Mostrando <strong>{startItem}–{endItem}</strong> de <strong>{filteredData.length}</strong>
          </span>
          {/* ReactPaginate original — props exactos */}
          <ReactPaginate
            previousLabel="Anterior"
            nextLabel="Siguiente"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            breakLabel="..."
            breakClassName="page-item"
            breakLinkClassName="page-link"
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={5}
            onPageChange={handlePageChange}
            containerClassName="pagination"
            activeClassName="active"
          />
        </div>
      )}

      {/* Badge preservado — import intacto */}
      {void [Badge]}
    </div>
  );
};

export default ReportTable;